<template>
  <!--
    DEVAMS-2156: Share Destination Picker — rendered globally from App.vue.
    Controlled by shareReceiverStore.pickerOpen.

    Step flow:
      1 → Choose destination type (Event / PlannedWork Task / Aircraft Handover Item)
      2 → Pick the specific record (searchable list)
      3 (Handover only) → Pick the specific handover item
  -->
  <!--
    @update:model-value handles outside-click / swipe-down dismissals.
    The pickerOpen setter intentionally does NOT call cancel() — that would
    create a double-cancel with @update:model-value. onSheetToggle is the
    single dismiss handler.
  -->
  <VDialog
    v-model="pickerOpen"
    width="85vw"
    max-width="85vw"
    :height="'85vh'"
    :fullscreen="false"
    scrollable
    @update:model-value="onSheetToggle"
  >
    <VCard>
      <!-- ── Header ─────────────────────────────────────────────── -->
      <VCardTitle class="d-flex align-center justify-space-between pa-4">
        <span>Attach "{{ pendingFile?.filename }}"</span>
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="cancel"
        />
      </VCardTitle>

      <!-- ── Editable filename (base name only — extension is locked) -->
      <div class="px-4 pb-2 pt-0">
        <VTextField
          v-model="customBasename"
          label="Filename"
          density="compact"
          variant="outlined"
          hide-details
          prepend-inner-icon="mdi-pencil-outline"
          :suffix="fileExtension"
        />
      </div>
      <VDivider />

      <!-- ── Confirm upload dialog ──────────────────────────────────── -->
      <VDialog
        v-model="confirmOpen"
        max-width="380"
        @update:model-value="(v) => { if (!v) confirmPending = null }"
      >
        <VCard>
          <VCardTitle class="text-h6 pa-4">
            Confirm upload
          </VCardTitle>
          <VCardText class="pa-4 pt-0">
            Upload <strong>{{ customBasename + fileExtension }}</strong> to
            <strong>{{ confirmDestLabel }}</strong>?
          </VCardText>
          <VCardActions class="pa-4 pt-0">
            <VSpacer />
            <VBtn
              variant="text"
              @click="confirmOpen = false"
            >
              Cancel
            </VBtn>
            <VBtn
              color="primary"
              variant="flat"
              :loading="store.uploading"
              @click="runConfirmedUpload"
            >
              Upload
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>

      <!-- ── File size guard ─────────────────────────────────────── -->
      <VAlert
        v-if="fileSizeError"
        type="error"
        density="compact"
        class="mx-4 mb-2"
      >
        {{ fileSizeError }}
      </VAlert>

      <VCardText class="pa-0">
        <!-- ── Step 1: Destination type ────────────────────────── -->
        <template v-if="step === 1">
          <VList>
            <VListSubheader>Where should this file go?</VListSubheader>

            <VListItem
              prepend-icon="mdi-calendar-star"
              title="Event"
              subtitle="Attach to a standalone event"
              @click="selectType('event')"
            />

            <VListItem
              prepend-icon="mdi-clipboard-text-outline"
              title="PlannedWork Task"
              subtitle="Attach to a task in your task list"
              @click="selectType('task')"
            />

            <!-- Images only -->
            <VListItem
              v-if="isImage"
              prepend-icon="mdi-file-eye-outline"
              title="Techrecord"
              subtitle="Add as a techrecord photo with page reference"
              @click="selectType('techrecord')"
            />

            <!-- Images only for handover items -->
            <VListItem
              v-if="isImage"
              prepend-icon="mdi-airplane-check"
              title="Aircraft Handover Item"
              subtitle="Add as annotated image on a handover item"
              @click="selectType('handover-item')"
            />
          </VList>
        </template>

        <!-- ── Step 2: Pick a record ──────────────────────────── -->
        <template v-else-if="step === 2">
          <div class="px-4 pb-2">
            <VBtn
              variant="text"
              prepend-icon="mdi-arrow-left"
              @click="goBack"
            >
              Back
            </VBtn>
          </div>

          <VTextField
            v-model="searchQuery"
            class="px-4 pb-2"
            placeholder="Search…"
            clearable
            hide-details
            density="compact"
            prepend-inner-icon="mdi-magnify"
          />

          <VProgressLinear
            v-if="loadingList"
            indeterminate
            color="primary"
          />

          <VAlert
            v-else-if="listError"
            type="error"
            density="compact"
            class="mx-4 mb-2"
          >
            {{ listError }}
          </VAlert>

          <!-- Event list -->
          <VList
            v-else-if="selectedType === 'event'"
            max-height="60vh"
            class="overflow-y-auto"
          >
            <VListItem
              v-if="filteredEvents.length === 0"
              title="No open events found"
              subtitle="Try a different search"
            />
            <VListItem
              v-for="event in filteredEvents"
              :key="event.id"
              :title="event.headline"
              :subtitle="event.acRegistration ?? ''"
              @click="selectRecord(event.id, event.headline)"
            />
          </VList>

          <!-- Task list (task, techrecord) -->
          <VList
            v-else-if="selectedType === 'task' || selectedType === 'techrecord'"
            max-height="60vh"
            class="overflow-y-auto"
          >
            <VListItem
              v-if="filteredMyTasks.length === 0 && filteredOtherTasks.length === 0"
              title="No tasks found"
              subtitle="Try a different search"
            />

            <template v-if="filteredMyTasks.length > 0">
              <VListSubheader>Your tasks</VListSubheader>
              <template
                v-for="task in filteredMyTasks"
                :key="task.id"
              >
                <VListItem
                  :prepend-avatar="getLogo(task.Flight?.PrefixIATA)"
                  :title="task.Description"
                  :subtitle="[task.Flight?.AircraftRegistration, task.Flight?.FlightName].filter(Boolean).join(' · ')"
                  @click="selectRecord(task.id!, task.Description)"
                >
                  <template #append>
                    <span class="text-caption text-disabled">{{ displayTaskTime(task) }}</span>
                  </template>
                </VListItem>
                <VDivider />
              </template>
            </template>

            <div
              v-if="filteredMyTasks.length > 0 && filteredOtherTasks.length > 0"
              class="py-3"
            />

            <template v-if="filteredOtherTasks.length > 0">
              <VListSubheader v-if="filteredMyTasks.length > 0">Other tasks</VListSubheader>
              <template
                v-for="task in filteredOtherTasks"
                :key="task.id"
              >
                <VListItem
                  :prepend-avatar="getLogo(task.Flight?.PrefixIATA)"
                  :title="task.Description"
                  :subtitle="[task.Flight?.AircraftRegistration, task.Flight?.FlightName].filter(Boolean).join(' · ')"
                  @click="selectRecord(task.id!, task.Description)"
                >
                  <template #append>
                    <span class="text-caption text-disabled">{{ displayTaskTime(task) }}</span>
                  </template>
                </VListItem>
                <VDivider />
              </template>
            </template>
          </VList>

          <!-- Handover: task picker first -->
          <VList
            v-else-if="selectedType === 'handover-item'"
            max-height="60vh"
            class="overflow-y-auto"
          >
            <VListItem
              v-if="filteredMyTasksWithHandovers.length === 0 && filteredOtherTasksWithHandovers.length === 0"
              title="No tasks with active handovers"
            />

            <template v-if="filteredMyTasksWithHandovers.length > 0">
              <VListSubheader>Your tasks</VListSubheader>
              <template
                v-for="task in filteredMyTasksWithHandovers"
                :key="task.id"
              >
                <VListItem
                  :prepend-avatar="getLogo(task.Flight?.PrefixIATA)"
                  :title="task.Description"
                  :subtitle="[task.Flight?.AircraftRegistration, task.Flight?.FlightName].filter(Boolean).join(' · ')"
                  @click="selectHandoverTask(task.id!)"
                >
                  <template #append>
                    <span class="text-caption text-disabled">{{ displayTaskTime(task) }}</span>
                  </template>
                </VListItem>
                <VDivider />
              </template>
            </template>

            <div
              v-if="filteredMyTasksWithHandovers.length > 0 && filteredOtherTasksWithHandovers.length > 0"
              class="py-3"
            />

            <template v-if="filteredOtherTasksWithHandovers.length > 0">
              <VListSubheader v-if="filteredMyTasksWithHandovers.length > 0">Other tasks</VListSubheader>
              <template
                v-for="task in filteredOtherTasksWithHandovers"
                :key="task.id"
              >
                <VListItem
                  :prepend-avatar="getLogo(task.Flight?.PrefixIATA)"
                  :title="task.Description"
                  :subtitle="[task.Flight?.AircraftRegistration, task.Flight?.FlightName].filter(Boolean).join(' · ')"
                  @click="selectHandoverTask(task.id!)"
                >
                  <template #append>
                    <span class="text-caption text-disabled">{{ displayTaskTime(task) }}</span>
                  </template>
                </VListItem>
                <VDivider />
              </template>
            </template>
          </VList>
        </template>

        <!-- ── Step 3: Techrecord page number ────────────────── -->
        <template v-else-if="step === 3 && selectedType === 'techrecord'">
          <div class="px-4 pb-2">
            <VBtn
              variant="text"
              prepend-icon="mdi-arrow-left"
              @click="step = 2"
            >
              Back
            </VBtn>
          </div>
          <div class="px-4 pb-4">
            <VTextField
              v-model="techrecordPageId"
              label="Techlog page reference"
              placeholder="e.g. TL-2024-001"
              :counter="25"
              maxlength="25"
              hide-details="auto"
              density="compact"
              class="mb-4"
            />
            <VBtn
              color="primary"
              block
              :disabled="!techrecordPageId.trim() || store.uploading"
              :loading="store.uploading"
              @click="uploadTechrecord"
            >
              Upload Techrecord
            </VBtn>
          </div>
        </template>

        <!-- ── Step 3: Handover item picker ───────────────────── -->
        <template v-else-if="step === 3">
          <div class="px-4 pb-2">
            <VBtn
              variant="text"
              prepend-icon="mdi-arrow-left"
              @click="step = 2"
            >
              Back
            </VBtn>
          </div>

          <VProgressLinear
            v-if="loadingHandoverItems"
            indeterminate
            color="primary"
          />

          <VAlert
            v-else-if="listError"
            type="error"
            density="compact"
            class="mx-4 mb-2"
          >
            {{ listError }}
          </VAlert>

          <VList
            v-else
            max-height="60vh"
            class="overflow-y-auto"
          >
            <VListSubheader>Select a handover item</VListSubheader>
            <VListItem
              v-if="handoverItems.length === 0"
              title="No handover items found"
            />
            <VListItem
              v-for="item in handoverItems"
              :key="item.id ?? 0"
              :title="item.typeOfWorkStatus"
              :subtitle="[item.woAtlRef, item.ata, item.pos].filter(Boolean).join(' · ')"
              @click="uploadToHandoverItem(item.id!, item.typeOfWorkStatus ?? 'handover item')"
            />
          </VList>
        </template>
      </VCardText>

      <!-- ── Footer: upload progress ──────────────────────────── -->
      <VCardActions
        v-if="store.uploading"
        class="px-4 pb-4"
      >
        <VProgressLinear
          indeterminate
          color="primary"
          class="w-100"
        />
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<script setup lang="ts">
import { useShareReceiverStore } from '@/stores/shareReceiver'
import { useAircraftHandoverStore } from '@/stores/aircraftHandoverStore'
import { useAuthStore } from '@/stores/auth'
import jwtInterceptor from '@/plugins/jwtInterceptor'
import * as Sentry from '@sentry/vue'
import logos from '@/plugins/logos'
import { stringtoDateTime } from '@/plugins/helpers'
import type { PlannedTask } from '@/interfaces/api/tasks'
import type { AppEvent } from '@/types/event'
import type { AircraftHandoverItem } from '@/types/aircraftHandover'

// ── Stores ────────────────────────────────────────────────────────────────────

const store = useShareReceiverStore()
const handoverStore = useAircraftHandoverStore()
const authStore = useAuthStore()

// ── Derived state from store ──────────────────────────────────────────────────

// pickerOpen is a plain read-through computed — the setter is intentionally a no-op
// because all dismiss flows (close button / outside click / swipe) go through
// onSheetToggle, which calls cancel(). A setter that also calls cancel() would
// cause a double-cancel because VBottomSheet updates v-model AND emits @update:model-value.
const pickerOpen = computed({
  get: () => store.pickerOpen,
  set: (_val: boolean) => {
    // intentional no-op — dismiss is handled exclusively in onSheetToggle
  },
})

// When the extension pre-selected a destination type, jump straight to step 2.
watch(pickerOpen, (open) => {
  if (open && store.pendingFile?.destinationType) {
    const type = store.pendingFile.destinationType as 'event' | 'task' | 'techrecord' | 'handover-item'
    selectType(type)
  }
})

const pendingFile = computed(() => store.pendingFile)

// Extension is locked; only the base name is editable.
const fileExtension = computed<string>(() => {
  const name = pendingFile.value?.filename ?? ''
  const dot = name.lastIndexOf('.')
  return dot !== -1 ? name.slice(dot) : ''
})

const customBasename = ref<string>('')

watch(pendingFile, (file) => {
  if (file) {
    const dot = file.filename.lastIndexOf('.')
    customBasename.value = dot !== -1 ? file.filename.slice(0, dot) : file.filename
  }
}, { immediate: true })

// ── Confirm upload dialog ─────────────────────────────────────────────────────

const confirmOpen = ref(false)
const confirmDestLabel = ref('')
const confirmPending = ref<(() => Promise<void>) | null>(null)

function requestConfirm(destLabel: string, uploadFn: () => Promise<void>): void {
  confirmDestLabel.value = destLabel
  confirmPending.value = uploadFn
  confirmOpen.value = true
}

async function runConfirmedUpload(): Promise<void> {
  confirmOpen.value = false
  await confirmPending.value?.()
  confirmPending.value = null
}

/** True when the shared file is an image type (JPEG, PNG, HEIC, etc.). */
const isImage = computed(() => {
  const mime = pendingFile.value?.mimeType ?? ''
  return mime.startsWith('image/')
})

// ── File size validation ──────────────────────────────────────────────────────

/** 25 MB for event/task, 10 MB for handover items. */
const MAX_SIZE_DEFAULT = 25 * 1024 * 1024
const MAX_SIZE_HANDOVER = 10 * 1024 * 1024

const fileSizeError = computed<string | null>(() => {
  const file = pendingFile.value
  if (!file) return null
  const size = file.data.size
  const limit = selectedType.value === 'handover-item' ? MAX_SIZE_HANDOVER : MAX_SIZE_DEFAULT
  if (size > limit) {
    const limitMb = (limit / 1024 / 1024).toFixed(0)
    const sizeMb = (size / 1024 / 1024).toFixed(1)
    return `File is too large (${sizeMb} MB). Maximum for this destination is ${limitMb} MB.`
  }
  return null
})

// ── Step state ────────────────────────────────────────────────────────────────

const step = ref<1 | 2 | 3>(1)
const selectedType = ref<'event' | 'task' | 'techrecord' | 'handover-item' | null>(null)
const selectedTaskId = ref<number | null>(null)
const techrecordPageId = ref<string>('')

function resetSteps(): void {
  step.value = 1
  selectedType.value = null
  selectedTaskId.value = null
  techrecordPageId.value = ''
  searchQuery.value = ''
  customBasename.value = ''
  confirmOpen.value = false
  confirmPending.value = null
  events.value = []
  tasks.value = []
  handoverItems.value = []
  listError.value = null
}

// ── Search ────────────────────────────────────────────────────────────────────

const searchQuery = ref<string>('')

// ── Data lists ────────────────────────────────────────────────────────────────

const events = ref<AppEvent[]>([])
const tasks = ref<PlannedTask[]>([])
const handoverItems = ref<AircraftHandoverItem[]>([])
const loadingList = ref<boolean>(false)
const loadingHandoverItems = ref<boolean>(false)
const listError = ref<string | null>(null)

const filteredEvents = computed<AppEvent[]>(() => {
  const q = searchQuery.value.toLowerCase()
  if (!q) return events.value
  return events.value.filter(
    (e) =>
      e.headline.toLowerCase().includes(q) ||
      (e.acRegistration ?? '').toLowerCase().includes(q),
  )
})

const filteredTasks = computed<PlannedTask[]>(() => {
  const q = searchQuery.value.toLowerCase()
  if (!q) return tasks.value
  return tasks.value.filter(
    (t) =>
      t.Description.toLowerCase().includes(q) ||
      t.TypeOfWork.toLowerCase().includes(q),
  )
})

/** Tasks that have an active (non-noted) handover. */
const filteredTasksWithHandovers = computed<PlannedTask[]>(() => {
  const q = searchQuery.value.toLowerCase()
  const candidates = tasks.value.filter((t) => t.hasHandovers)
  if (!q) return candidates
  return candidates.filter(
    (t) =>
      t.Description.toLowerCase().includes(q) ||
      t.TypeOfWork.toLowerCase().includes(q),
  )
})

const currentUsersign = computed<string | null>(() => authStore.getUsersign ?? null)

const myTasks = computed<PlannedTask[]>(() =>
  tasks.value.filter(t =>
    currentUsersign.value &&
    t.plannedWorkAttendanceStates?.some(s => s.user?.usersign === currentUsersign.value)
  )
)

const otherTasks = computed<PlannedTask[]>(() =>
  tasks.value.filter(t =>
    !currentUsersign.value ||
    !t.plannedWorkAttendanceStates?.some(s => s.user?.usersign === currentUsersign.value)
  )
)

const filteredMyTasks = computed<PlannedTask[]>(() => {
  const q = searchQuery.value.toLowerCase()
  if (!q) return myTasks.value
  return myTasks.value.filter(t =>
    t.Description.toLowerCase().includes(q) ||
    (t.Flight?.AircraftRegistration ?? '').toLowerCase().includes(q)
  )
})

const filteredOtherTasks = computed<PlannedTask[]>(() => {
  const q = searchQuery.value.toLowerCase()
  if (!q) return otherTasks.value
  return otherTasks.value.filter(t =>
    t.Description.toLowerCase().includes(q) ||
    (t.Flight?.AircraftRegistration ?? '').toLowerCase().includes(q)
  )
})

const myTasksWithHandovers = computed<PlannedTask[]>(() =>
  myTasks.value.filter(t => t.hasHandovers)
)
const otherTasksWithHandovers = computed<PlannedTask[]>(() =>
  otherTasks.value.filter(t => t.hasHandovers)
)
const filteredMyTasksWithHandovers = computed<PlannedTask[]>(() => {
  const q = searchQuery.value.toLowerCase()
  if (!q) return myTasksWithHandovers.value
  return myTasksWithHandovers.value.filter(t =>
    t.Description.toLowerCase().includes(q) ||
    (t.Flight?.AircraftRegistration ?? '').toLowerCase().includes(q)
  )
})
const filteredOtherTasksWithHandovers = computed<PlannedTask[]>(() => {
  const q = searchQuery.value.toLowerCase()
  if (!q) return otherTasksWithHandovers.value
  return otherTasksWithHandovers.value.filter(t =>
    t.Description.toLowerCase().includes(q) ||
    (t.Flight?.AircraftRegistration ?? '').toLowerCase().includes(q)
  )
})

function getLogo(iata?: string | null): string {
  if (iata && logos[iata + '.png']) return logos[iata + '.png'] as string
  return logos['nayak.png'] as string
}

function displayTaskTime(task: PlannedTask): string {
  const f = task.Flight
  if (f?.ActualInBlockTime) return stringtoDateTime(f.ActualInBlockTime, 'd MMM HH:mm')
  if (f?.EstimatedInBlockTime) return stringtoDateTime(f.EstimatedInBlockTime, 'd MMM HH:mm')
  if (f?.ScheduledInBlockTime) return stringtoDateTime(f.ScheduledInBlockTime, 'd MMM HH:mm')
  if (task.Start) return stringtoDateTime(task.Start, 'd MMM HH:mm')
  return ''
}

// ── Navigation ────────────────────────────────────────────────────────────────

async function selectType(type: 'event' | 'task' | 'techrecord' | 'handover-item'): Promise<void> {
  selectedType.value = type
  step.value = 2
  searchQuery.value = ''
  listError.value = null
  await loadList(type)
}

function goBack(): void {
  step.value = 1
  selectedType.value = null
  searchQuery.value = ''
  listError.value = null
}

async function loadList(type: 'event' | 'task' | 'handover-item'): Promise<void> {
  loadingList.value = true
  try {
    if (type === 'event') {
      const response = await jwtInterceptor.get<{ items: AppEvent[] }>('v1/events', {
        params: { open: '1' },
      })
      events.value = response?.data?.items ?? []
    } else {
      // task, techrecord, and handover-item all need the task list.
      const response = await jwtInterceptor.get<{ plannedWork: PlannedTask[] }>(
        'v1/user-tasks/overview',
      )
      tasks.value = response?.data?.plannedWork ?? []
    }
  } catch (error) {
    listError.value = 'Failed to load list. Please try again.'
    Sentry.captureException(error, {
      tags: { component: 'ShareDestinationPicker', action: 'loadList', type },
    })
  } finally {
    loadingList.value = false
  }
}

async function selectRecord(id: number, label: string): Promise<void> {
  if (!selectedType.value) return
  if (fileSizeError.value) return

  // Techrecord needs a page reference before uploading → go to step 3
  if (selectedType.value === 'techrecord') {
    selectedTaskId.value = id
    step.value = 3
    return
  }

  const type = selectedType.value as 'event' | 'task' | 'handover-item'
  requestConfirm(label, async () => {
    try {
      store.renameFile(customBasename.value + fileExtension.value)
      await store.uploadToDestination(type, id)
      emitter.emit('snackbar', {
        snackbarText: 'File attached successfully.',
        snackColor: 'success',
        snackIcon: 'mdi-check-circle-outline',
      } as snackevent)
    } catch (_error) {
      emitter.emit('snackbar', {
        snackbarText: store.error ?? 'Upload failed. Please try again.',
        snackColor: 'error',
        snackIcon: 'mdi-alert-circle-outline',
      } as snackevent)
    }
  })
}

async function selectHandoverTask(taskId: number): Promise<void> {
  selectedTaskId.value = taskId
  step.value = 3
  listError.value = null
  loadingHandoverItems.value = true
  try {
    // Load handovers for the selected task to get its items.
    await handoverStore.fetchHandovers(taskId)
    // Collect items from the draft or issued handover (the one the user is working on).
    const activeHandover =
      handoverStore.draftHandover ?? handoverStore.issuedHandover
    handoverItems.value = activeHandover?.items ?? []
  } catch (error) {
    listError.value = 'Failed to load handover items.'
    Sentry.captureException(error, {
      tags: { component: 'ShareDestinationPicker', action: 'selectHandoverTask', taskId },
    })
  } finally {
    loadingHandoverItems.value = false
  }
}

async function uploadToHandoverItem(itemId: number, label: string): Promise<void> {
  const handoverId =
    handoverStore.draftHandover?.id ?? handoverStore.issuedHandover?.id
  if (!handoverId || !selectedTaskId.value) return
  if (fileSizeError.value) {
    emitter.emit('snackbar', {
      snackbarText: fileSizeError.value,
      snackColor: 'error',
    } as snackevent)
    return
  }

  requestConfirm(label, async () => {
    try {
      store.renameFile(customBasename.value + fileExtension.value)
      await store.uploadToDestination('handover-item', handoverId, itemId)
      emitter.emit('snackbar', {
        snackbarText: 'Image attached to handover item.',
        snackColor: 'success',
        snackIcon: 'mdi-check-circle-outline',
      } as snackevent)
    } catch (_error) {
      emitter.emit('snackbar', {
        snackbarText: store.error ?? 'Upload failed. Please try again.',
        snackColor: 'error',
        snackIcon: 'mdi-alert-circle-outline',
      } as snackevent)
    }
  })
}

// ── Cancel ────────────────────────────────────────────────────────────────────

async function uploadTechrecord(): Promise<void> {
  if (!selectedTaskId.value || !techrecordPageId.value.trim()) return
  const pageRef = techrecordPageId.value.trim()
  const taskId = selectedTaskId.value
  requestConfirm(`techrecord (page ${pageRef})`, async () => {
    try {
      store.renameFile(customBasename.value + fileExtension.value)
      await store.uploadTechrecord(taskId, pageRef)
      emitter.emit('snackbar', {
        snackbarText: 'Techrecord uploaded successfully.',
        snackColor: 'success',
        snackIcon: 'mdi-check-circle-outline',
      } as snackevent)
    } catch (_error) {
      emitter.emit('snackbar', {
        snackbarText: store.error ?? 'Upload failed. Please try again.',
        snackColor: 'error',
        snackIcon: 'mdi-alert-circle-outline',
      } as snackevent)
    }
  })
}

async function cancel(): Promise<void> {
  await store.clearPendingFile()
  resetSteps()
}

function onSheetToggle(val: boolean): void {
  if (!val) cancel()
}
</script>
